import React from 'react';
import type { ProjectTask } from '../../types';
import type { ShopScheduleViewModel } from '../../utils/shopScheduleView';

export interface ShopScheduleTimelineProps {
  model: ShopScheduleViewModel;
  onlyOverlaps: boolean;
  cellWidth: number;
  rowHeight: number;
  labelWidth: number;
  /** Larger type for display / TV */
  displayMode?: boolean;
}

const ShopScheduleTimeline: React.FC<ShopScheduleTimelineProps> = ({
  model,
  onlyOverlaps,
  cellWidth,
  rowHeight,
  labelWidth,
  displayMode = false,
}) => {
  const { filteredTasks, projectsOrdered, overlappingTaskIds, days, tasksByProject, dayIndex } = model;

  const totalW = days.length * cellWidth;
  const rootClass = displayMode ? 'ps-gantt-wrap ps-timeline ps-timeline--display' : 'ps-gantt-wrap ps-timeline';

  return (
    <div className={`${rootClass} pm-scroll`}>
      <div className="ps-gantt-inner" style={{ minWidth: labelWidth + totalW }}>
        <div className="ps-grid-head">
          <div className="ps-head-spacer" style={{ width: labelWidth }} />
          <div className="ps-day-head" style={{ width: totalW }}>
            {days.map((d, i) => (
              <div
                key={i}
                className={`ps-day-cell ${d.getDay() === 0 || d.getDay() === 6 ? 'weekend' : ''}`}
                style={{ width: cellWidth }}
                title={d.toLocaleDateString()}
              >
                {!displayMode ? (
                  i === 0 || d.getDate() === 1 ? <span className="ps-day-label">{d.getDate()}</span> : null
                ) : d.getDay() === 1 || i === 0 ? (
                  <span className="ps-day-label">
                    {d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {projectsOrdered.map((p) => {
          const raw = tasksByProject.get(p.id!) || [];
          const list = onlyOverlaps ? raw.filter((t) => t.id && overlappingTaskIds.has(t.id)) : raw;
          if (onlyOverlaps && list.length === 0) return null;

          return (
            <div key={p.id} className="ps-row" style={{ minHeight: rowHeight }}>
              <div className="ps-row-label" style={{ width: labelWidth }}>
                <span className="ps-proj-name">{p.name}</span>
                {p.location && <span className="ps-proj-meta">{p.location}</span>}
              </div>
              <div className="ps-row-chart" style={{ width: totalW, height: rowHeight }}>
                {days.map((d, i) => {
                  const wk = d.getDay() === 0 || d.getDay() === 6;
                  if (!wk) return null;
                  return (
                    <div
                      key={`w-${i}`}
                      className="ps-weekend"
                      style={{ left: i * cellWidth, width: cellWidth, height: rowHeight }}
                    />
                  );
                })}
                {list.map((t: ProjectTask) => {
                  const si = Math.max(0, dayIndex(new Date(t.startDate)));
                  const ei = Math.max(si, dayIndex(new Date(t.endDate)));
                  const overlap = !!(t.id && overlappingTaskIds.has(t.id));
                  const dept = t.department;
                  const w = Math.max((ei - si + 1) * cellWidth, cellWidth);
                  return (
                    <div
                      key={t.id}
                      className={`ps-bar ps-bar--${dept} ${overlap ? 'ps-bar-overlap' : ''}`}
                      style={{
                        left: si * cellWidth,
                        width: w,
                        maxWidth: totalW - si * cellWidth,
                      }}
                      title={`${t.title} (${dept})`}
                    >
                      {w > (displayMode ? 80 : 56) && <span className="ps-bar-text">{t.title}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {filteredTasks.length === 0 && (
          <div className="ps-empty">No production or installation tasks yet. Set task department in the task panel.</div>
        )}
      </div>
    </div>
  );
};

export default ShopScheduleTimeline;
